import React, { useEffect, useState } from 'react'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { withTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import styled, { withTheme } from 'styled-components'
import parseJSON from 'date-fns/parseJSON'

import {
  programPublishAttempts,
  programs as programsResource,
  shifts as shiftsResource,
} from '../../redux/actions/resource'
import { getProgramById, getAllShiftsForProgram } from '../../redux/reducers'

import Action, { Actions } from '../../components/Action'
import { confirm } from '../../components/Alert'
import { Button } from '../../components/forms/buttons'
import Table, { Body, Column, Head, Header, Row } from '../../components/Table'
import Icon from '../../components/Icon'
import Toolbar from '../../components/Toolbar'
import ListToolbar from '../../components/ListToolbar'
import Tile, { Grid, LabelledValue } from '../../components/Tile'
import { TextLink } from '../../components/type'
import ListTitle from '../../components/composed/ListTitle'
import { request, url } from '../../lib/api'
import { getStoredToken } from '../../lib/auth'
import { addFlashMessage } from '../../redux/actions'
import DragHandle from '../../components/drag-and-drop/DragHandle'
import SortableItem from '../../components/drag-and-drop/SortableItem'
import SortableList from '../../components/drag-and-drop/SortableList'
import useSortableOrder from '../../components/drag-and-drop/useSortableOrder'

const ShiftTitle = styled.span`
  display: block;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 0 24px;
  text-align: center;
  overflow-wrap: anywhere;
`

const ProgramShow = ({ history, match, t }) => {
  const programId = match.params.id
  const token = getStoredToken()
  const program = useSelector((state) => getProgramById(state, programId))
  const { name, id, startsAt, endsAt, availableActions, state } = program || {}

  const shifts = useSelector((state) => getAllShiftsForProgram(state, id))
  const dispatch = useDispatch()

  const [pendingWorkflow, setPendingWorkflow] = useState()
  const [searchActive, setSearchActive] = useState(false)
  const {
    isSaving: savingShiftOrder,
    onDragEnd,
    orderedIds: orderedShiftIds,
  } = useSortableOrder({
    items: shifts,
    updatePosition: (shiftId, sortOrder) =>
      dispatch(shiftsResource.update(shiftId, {}, { sort_order: sortOrder }))
        .promise,
    reload: () => dispatch(shiftsResource.list({ programId })),
    onError: () =>
      dispatch(addFlashMessage('Fehler', t('errors:shift.couldNotReorder'))),
    disabled: searchActive,
  })

  const orderedShifts = orderedShiftIds
    .map((shiftId) =>
      shifts.find(({ id: currentId }) => String(currentId) === String(shiftId))
    )
    .filter(Boolean)

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (programId) {
      dispatch(programsResource.read(programId))
      dispatch(shiftsResource.list({ programId }))
    }
  }, [dispatch, programId])

  const onSearch = (search) => {
    if (programId) {
      setSearchActive(Boolean(search))
      dispatch(shiftsResource.list({ programId, search }))
    }
  }

  const publishProgramm = (workflow) => {
    setPendingWorkflow(workflow)
    dispatch(
      programPublishAttempts.create({ programId }, { workflow, wait: 0 })
    )
  }

  const onDeleteProgram = async () => {
    if (await confirm('Möchtest du dieses Programm wirklich löschen')) {
      dispatch(programsResource.destroy(programId)).promise.then(() => {
        history.push('/programs')
      })
    }
  }

  const onDeleteShift = async (id) => {
    if (await confirm('Möchtest du diesen Turnus wirklich löschen?')) {
      dispatch(shiftsResource.destroy(id))
    }
  }

  const onDuplicateShift = async (shiftId) => {
    if (await confirm('Möchtest du diesen Turnus wirklich duplizieren?')) {
      try {
        await request(`/shifts/${shiftId}/duplicate`, 'POST')

        dispatch(shiftsResource.list({ programId }))
      } catch (error) {
        dispatch(addFlashMessage('Fehler', t('errors:shift:couldNotDuplicate')))
      }
    }
  }

  const onDuplicateProgram = async (id) => {
    try {
      const response = await fetch(`${url}/programs/${id}/duplicate`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      if (!response.ok) {
        dispatch(
          addFlashMessage('Fehler', t('errors:program:couldNotDuplicate'))
        )
      } else {
        await response.json()
        history.push('/programs')
      }
    } catch (error) {
      console.log('Error duplicating program')
    }
  }

  return (
    <>
      <Toolbar title={name} onBack={() => history.push('/programs')}>
        <Action to={`/programs/${programId}/edit`}>
          <Icon name="edit" />
        </Action>
        <Button onClick={() => onDuplicateProgram(programId)}>
          <div>{t('duplicate')}</div>
        </Button>
      </Toolbar>
      <Table attributes>
        <Body>
          <Row>
            <Header scope="row">{t('attributes:program.name')}:</Header>
            <Column>{name}</Column>
          </Row>
          <Row>
            <Header scope="row">{t('attributes:program.startsAt')}:</Header>
            <Column>
              {t('format:dateTimeLong', { date: parseJSON(startsAt) })}
            </Column>
          </Row>
          <Row>
            <Header scope="row">{t('attributes:program.endsAt')}:</Header>
            <Column>
              {t('format:dateTimeLong', { date: parseJSON(endsAt) })}
            </Column>
          </Row>
          <Row>
            <Header scope="row">{t('attributes:program.state')}:</Header>
            <Column>{t(`attributes:program.states.${state}`)}</Column>
          </Row>
          <Row>
            <Column colSpan={2}>
              <Actions>
                {Object.keys(availableActions || {})
                  .filter((key) => availableActions[key])
                  .map((workflow) => (
                    <Button
                      key={workflow}
                      onClick={() => publishProgramm(workflow)}
                      waiting={pendingWorkflow === workflow}
                    >
                      {t(`actions:program.${workflow}`)}
                    </Button>
                  ))}
              </Actions>
            </Column>
          </Row>
        </Body>
      </Table>
      <ListTitle>
        <span>{t('models:shift', { count: 0 })}</span>
        <Action to={`/programs/${programId}/shifts/new`}>
          <Icon small name="add" />
        </Action>
      </ListTitle>
      <ListToolbar onSearch={onSearch}>
        {({ showTiles }) => {
          return (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              {showTiles ? (
                <Grid>
                  <SortableList
                    items={orderedShifts.map(({ id }) => id)}
                    strategy={rectSortingStrategy}
                  >
                    {orderedShifts.map(
                      ({ name, id, startsAt, endsAt, summary }) => {
                        return (
                          <SortableItem
                            key={id}
                            id={id}
                            disabled={searchActive || savingShiftOrder}
                          >
                            {({
                              attributes,
                              disabled,
                              listeners,
                              setActivatorNodeRef,
                            }) => (
                              <Tile
                                title={
                                  <ShiftTitle>
                                    <DragHandle
                                      ref={setActivatorNodeRef}
                                      type="button"
                                      disabled={disabled}
                                      aria-label={t('actions:shift.reorder')}
                                      {...attributes}
                                      {...listeners}
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                    >
                                      <Icon
                                        small
                                        name="bars"
                                        bordered={false}
                                      />
                                    </DragHandle>
                                    {name}
                                  </ShiftTitle>
                                }
                                onClick={() => history.push(`/shifts/${id}`)}
                                footer={`${t('format:dateTimeShort', {
                                  date: parseJSON(startsAt),
                                })} - ${t('format:dateTimeShort', {
                                  date: parseJSON(endsAt),
                                })}`}
                                centered
                                actions={
                                  <>
                                    <Action
                                      onClick={(e) => {
                                        onDuplicateShift(id)
                                        e.stopPropagation()
                                      }}
                                    >
                                      <Icon
                                        name="copy"
                                        color="#fff"
                                        border="#fff"
                                      />
                                    </Action>
                                    <Action
                                      onClick={(e) => {
                                        onDeleteShift(id)
                                        e.stopPropagation()
                                      }}
                                    >
                                      <Icon
                                        name="delete"
                                        color="#fff"
                                        border="#fff"
                                      />
                                    </Action>
                                  </>
                                }
                              >
                                {(summary || []).map(
                                  ({ entity_type, scope, count }, index) => {
                                    let txtKey = null
                                    if (scope == null) {
                                      txtKey = `models:${entity_type}`
                                    } else if (scope == 'unsuspended') {
                                      txtKey = `attributes:${entity_type}:modes:0`
                                    } else if (scope == 'suspended') {
                                      txtKey = `attributes:${entity_type}:modes:1`
                                    } else if (
                                      scope == 'with_sensor_based_irrigation'
                                    ) {
                                      txtKey = `attributes:${entity_type}:modes:2`
                                    }

                                    return (
                                      <LabelledValue
                                        key={index}
                                        value={count}
                                        label={t(txtKey, { count: count })}
                                      />
                                    )
                                  }
                                )}
                              </Tile>
                            )}
                          </SortableItem>
                        )
                      }
                    )}
                  </SortableList>
                </Grid>
              ) : (
                <Table spacious divided>
                  <Head>
                    <Row>
                      <Header colSpan={2}>
                        {t('models:shift', { count: 0 })}
                      </Header>
                    </Row>
                  </Head>
                  <Body>
                    <SortableList
                      items={orderedShifts.map(({ id }) => id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {orderedShifts.map(({ name, id }) => {
                        return (
                          <SortableItem
                            key={id}
                            id={id}
                            disabled={searchActive || savingShiftOrder}
                            container={Row}
                          >
                            {({
                              attributes,
                              listeners,
                              setActivatorNodeRef,
                            }) => (
                              <>
                                <Column>
                                  <span
                                    ref={setActivatorNodeRef}
                                    {...attributes}
                                    {...listeners}
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <Icon small name="bars" bordered={false} />
                                  </span>{' '}
                                  <TextLink to={`/shifts/${id}`}>
                                    {name}
                                  </TextLink>
                                </Column>
                                <Column actions>
                                  <Action horizontal to={`/shifts/${id}`}>
                                    <Icon small name="view" />
                                  </Action>
                                  <Action horizontal to={`/shifts/${id}/edit`}>
                                    <Icon small name="edit" />
                                  </Action>
                                  <Action
                                    horizontal
                                    onClick={(e) => {
                                      onDuplicateShift(id)
                                      e.stopPropagation()
                                    }}
                                  >
                                    <Icon small name="copy" />
                                  </Action>
                                  <Action
                                    horizontal
                                    onClick={(e) => {
                                      onDeleteShift(id)
                                      e.stopPropagation()
                                    }}
                                  >
                                    <Icon small name="delete" />
                                  </Action>
                                </Column>
                              </>
                            )}
                          </SortableItem>
                        )
                      })}
                    </SortableList>
                  </Body>
                </Table>
              )}
            </DndContext>
          )
        }}
      </ListToolbar>
      <Grid>
        <Action to={`/programs/${programId}/shifts/new`}>
          <Icon name="add" />
        </Action>
      </Grid>
    </>
  )
}

export default withRouter(withTranslation()(withTheme(ProgramShow)))
